import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, tap, throwError, interval, Subscription } from 'rxjs';
import { StorageService } from './storage.service';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import * as signalR from '@microsoft/signalr';
import { Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { AuthApiService } from './auth-api.service';
import { UserApiService } from './user-api.service';
import { CrewSelfService } from './crew-self.service';
import { ErrorModalComponent } from 'src/app/auth/error-modal/error-modal.component';
import { distinctUntilChanged, finalize } from 'rxjs/operators';
import { BatteryOptimization } from '@capawesome-team/capacitor-android-battery-optimization';
import { AppConfig } from 'src/app/app.config';
import { jwtDecode } from 'jwt-decode';
import { StateData } from 'src/app/auth/auth.component';
import { LoginTrackingService, LOGIN_STAGE } from './login-tracking.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public platform: string = Capacitor.getPlatform();
  public isNative: boolean = Capacitor.isNativePlatform();
  public srHubconnection: signalR.HubConnection | undefined;
  public isLoginInProgress: boolean = false;

  public isOnlineSubject = new BehaviorSubject<boolean>(true); // Default online
  isOnline$ = this.isOnlineSubject.asObservable().pipe(distinctUntilChanged()); // Observable to subscribe to

  private signalRToken: string = '';
  public b2cCodeVerifier: string = '';
  public b2cAuthCodeBySignalR: string = '';
  public b2cLoginStateBySignalR: string = '';

  private refreshTokenInProgress = false;
  public browserFinished: boolean = false;
  public lastUrl = '';
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private connectionTimeSubscription: Subscription | undefined;
  private appActiveSubject = new BehaviorSubject<boolean>(true);
  appActive$ = this.appActiveSubject.asObservable();

  // Tracks whether we registered the browserFinished listener
  private browserListenerRegistered = false;
  private tokenExchangeInProgress = false;
  /** OAuth `state` values already claimed for callback processing (survives AuthComponent remount). */
  private readonly processedOAuthStates = new Set<string>();

  private signalRStartInProgress = false;
  private signalRRetryTimeout: ReturnType<typeof setTimeout> | null = null;
  private signalRTimerWasOffline = false;
  private signalRConnectionCheckInProgress = false;  
  private lastTrackedSignalRTokenRemainingSeconds: number | null = null;
  private signalRServerIssuedAtSeconds = 0;
  private signalRServerTimeReference = 0;

  private signOutInProgress = false;

  constructor(
    private readonly router: Router,
    private readonly authApiService: AuthApiService,
    private readonly _storageService: StorageService,
    private readonly userApiService: UserApiService,
    private readonly crewSelfService: CrewSelfService,
    private readonly modalController: ModalController,
    private readonly loginTracking: LoginTrackingService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    console.log('AuthService constructor initialized');
  }

  /**
   * Opens the Capacitor in-app browser and registers a ONE-TIME
   * browserFinished listener so we never stack duplicate listeners.
   */
  async openBrowser(url: string): Promise<void> {

    // Remove any existing listener before registering a fresh one
    if (this.browserListenerRegistered) {
      await Browser.removeAllListeners();
      this.browserListenerRegistered = false;
    }

    // Register listener BEFORE opening so we never miss the event
    await Browser.addListener('browserFinished', () => {
      console.log('AuthService: browserFinished fired');
      if (this.isAppActive) {
        this.onBrowserFinished();
      }
    });
    this.browserListenerRegistered = true;

    await Browser.open({ url });
  }

  /**
   * Called every time the in-app browser is dismissed.
   *
   * Scenarios:
   *  1. browserFinished = true  → browser was closed PROGRAMMATICALLY by us
   *                               (SignalR auth code received or token expired).
   *                               Do nothing — the calling code handles next step.
   *
   *  2. browserFinished = false → user MANUALLY closed the browser before auth
   *                               completed. Re-initiate the full login flow.
   */
  private onBrowserFinished = async (): Promise<void> => {
    console.log('AuthService: onBrowserFinished fired');
    console.log('AuthService: browserFinished flag =', this.browserFinished);

    // Clean up listener — fire only once per open
    await Browser.removeAllListeners();
    this.browserListenerRegistered = false;

    // Browser was closed by us programmatically — nothing to do here
    if (this.browserFinished) {
      console.log('AuthService: Browser closed programmatically, skipping re-initiation');
      this.loginTracking.track(
        LOGIN_STAGE.BROWSER_CLOSED,
        'Browser closed programmatically, skipping re-initiation.'
      );
      return;
    }

    // App went to background — ignore
    if (!this.isAppActive) {
      console.log('AuthService: App not active, skipping re-initiation');
      this.loginTracking.track(
        LOGIN_STAGE.BROWSER_CLOSED,
        'Browser found closed when app not active.'
      );
      return;
    }

    // ── User manually closed the browser ──────────────────────────────────────
    console.log('AuthService: User manually closed browser — re-initiating login');
    this.loginTracking.track(
      LOGIN_STAGE.BROWSER_CLOSED,
      'Browser closed by user > APP re-initiating login.'
    );

    // Reset flags so signIn() is not blocked
    this.isLoginInProgress = false;
    this.signOutInProgress = false;
    this.b2cAuthCodeBySignalR = '';
    this.b2cCodeVerifier = '';
    this.lastUrl = '';

    // Stop any in-flight SignalR connection
    this.connectionTimeSubscription?.unsubscribe();
    await this.srHubconnection?.stop();
    this.signalRToken = '';
    this.b2cLoginStateBySignalR = '';

    // Re-initiate login from scratch
    await this.signIn();
  };

  setAppActiveState(isActive: boolean) {
    console.log('AuthService: setAppActiveState =', isActive);
    this.appActiveSubject.next(isActive);
  }

  get isAppActive(): boolean {
    return this.appActiveSubject.value;
  }

  /**
   * Claims an OAuth `state` for callback processing exactly once app-wide.
   * Survives AuthComponent destroy/recreate while /auth?code&state remains in the URL.
   */
  claimOAuthCallback(state: string): boolean {
    if (!state) {
      return false;
    }
    if (this.processedOAuthStates.has(state)) {
      return false;
    }
    this.processedOAuthStates.add(state);
    return true;
  }

  async acquireB2CAccessToken(code: string, session_id?: string) {
    if (this.tokenExchangeInProgress) {
      this.loginTracking.track(
        LOGIN_STAGE.TOKEN_EXCHANGE_DUPLICATE,
        'Duplicate token exchange attempt ignored.',
        session_id
      );
      return;
    }

    this.tokenExchangeInProgress = true;

    const enc_code_verifier = this.b2cCodeVerifier || this._storageService.getDataFromStorage('code_verifier');
    this.loginTracking.track(
      LOGIN_STAGE.TOKEN_EXCHANGE_INIT,
      `Token exchange initiated. hasCode=${!!code}, codeLength=${code?.length ?? 0}, hasStoredSignalRCode=${!!this.b2cAuthCodeBySignalR}, hasVerifier=${!!enc_code_verifier}, isLoginInProgress=${this.isLoginInProgress}`,
      session_id
    );
    
    if (!code || !enc_code_verifier) {
      console.error('Token exchange failed due to missing code or code verifier');

      this.loginTracking.track(
        LOGIN_STAGE.TOKEN_EXCHANGE_FAILED,
        `Token exchange failed with hasCode=${!!code}, hasCodeVerifier=${!!enc_code_verifier}`,
        session_id
      );

      // clear current login transaction state
      this.clearLoginTransactionState();

      this.showLoginErrorModal();

      return;
    }

    let code_verifier: string | undefined;

    try{
      code_verifier = this.b2cCodeVerifier ? enc_code_verifier : this.crewSelfService.decryptData(enc_code_verifier)?.['code_verifier'];
    } catch (err) {
      const errorMessage = this.getErrorMessage(err);

      console.error(`Token exchange failed with error: ${errorMessage}`);

      this.loginTracking.track(
        LOGIN_STAGE.TOKEN_EXCHANGE_FAILED,
        `Code verifier decoding failed with error: ${errorMessage}`,
        session_id
      );

      // clear current login transaction state
      this.clearLoginTransactionState();

      this.showLoginErrorModal();

      return;
    }

    if (!code_verifier) {
      console.error('Token exchange failed because code verifier is missing after decoding');

      this.loginTracking.track(
        LOGIN_STAGE.TOKEN_EXCHANGE_FAILED,
        'Token exchange failed because code verifier is missing after decoding.',
        session_id
      );

      this.clearLoginTransactionState();
      this.showLoginErrorModal();

      return;
    }

    if (!this.isOnlineSubject.value) {
      console.warn("AuthService: No internet connection. Sign-in aborted.");
      this.loginTracking.track(
        LOGIN_STAGE.TOKEN_EXCHANGE_FAILED,
        'Token exchange failed because device is offline',
        session_id
      );

      // clear current login transaction state
      this.clearLoginTransactionState();

      return;
    }

    this.authApiService.b2cTokenApi(code, code_verifier).pipe(
      finalize(() => {
        this.tokenExchangeInProgress = false;
      })
    ).subscribe({
      next: (response: any) => {

        // clear current login transaction state
        this.clearLoginTransactionState(false);
        
        this.loginTracking.track(
          LOGIN_STAGE.TOKEN_EXCHANGE_RESPONSE,
          `B2C token API succeeded with hasAccessToken: ${!!response?.access_token}, hasRefreshToken: ${!!response?.refresh_token}`,
          session_id
        );

        if (!this.validateB2CToken(response.access_token, session_id)) {
          console.error('Invalid user token detected, signing out');

          this.loginTracking.track(
            LOGIN_STAGE.TOKEN_VALIDATION_FAILED,
            'B2C access token validation failed',
            session_id
          );          

          this.showLoginErrorModal();
          return;
        }

        this.loginTracking.track(
          LOGIN_STAGE.TOKEN_EXCHANGE_SUCCESS,
          'Access token acquired successfully.',
          session_id
        );
        this._storageService.putDataInStorage(
          'access_token',
          this.crewSelfService.encryptData({ access_token: response.access_token })
        );
        this._storageService.putDataInStorage(
          'refresh_token',
          this.crewSelfService.encryptData({ refresh_token: response.refresh_token })
        );
        console.log('Tokens saved, loading user details');
        this.getApprovedUserDetail(session_id);
      },
      error: (err: any) => {
        console.error('Error fetching access_token:', err);

        // clear current login transaction state
        this.clearLoginTransactionState(false);

        const errorMessage = this.getErrorMessage(err);
        const errorCode = err?.error?.error || 'unknown_error';

        this.loginTracking.track(
          LOGIN_STAGE.TOKEN_EXCHANGE_FAILED,
          `Token exchange failed with error: ${errorCode}, description: ${errorMessage}`,
          session_id
        );

        this.showLoginErrorModal();
      }
    });
  }

  private clearLoginTransactionState(
    clearTokenExchangeInProgress: boolean = true
  ): void {
    this.b2cAuthCodeBySignalR = '';
    this.b2cCodeVerifier = '';
    this._storageService.removeDataFromStorage('code_verifier');

    this.isLoginInProgress = false;
    this.signOutInProgress = false;

    if(clearTokenExchangeInProgress) {
      this.tokenExchangeInProgress = false;
    }
  }

  public getCrewBasicNotifications() {
    console.log('Fetching crew basic notifications');
    this.userApiService.getCrewBasicNotifications().subscribe({
      next: (response) => {
        if (response.data.IS_UPDATED) {
          this.crewSelfService.isBasicNotificationSet(response.data);
        }
      },
      error: (err: any) => {
        console.error('Error fetching default notifications:', err);
      }
    });
  }

  public getApprovedUserDetail(session_id?:string) {
    console.log('Fetching approved user details');
    this.userApiService.fetchUserApprovedDetail()
      .subscribe({
        next: (response) => {
          if (response.status === 'success') {
            this.loginTracking.track(LOGIN_STAGE.LOGIN_SUCCESS, 'User profile loaded successfully', session_id);
            this.getCrewBasicNotifications();
            this.router.navigate(['home']);
            this.crewSelfService.isFillipinoSet(response.data.POOL_CODE);
            this.crewSelfService.isPoolCodeSet(response.data.POOL_CODE);
            this.crewSelfService.isSetActiveUser(response.data);
          } else {
            console.log('getApprovedUserDetail > response', response)
            console.error('Fetching user details failed with status:', response.status);
            this.loginTracking.track(
              LOGIN_STAGE.PROFILE_FETCH_FAILED, 
              `Fetching user details failed with status: ${response.status}`,
              session_id
            );
          }
        },
        error: (err: any) => {
          console.error('Error user approved detail:', err);
          const errorMessage = this.getErrorMessage(err);
          this.loginTracking.track(
            LOGIN_STAGE.PROFILE_FETCH_FAILED, 
            `Fetching user details failed with error: ${errorMessage}`,
            session_id
          );
        }
      });
  }

  redirectToB2c(code_challange: string, logout?: boolean, access_denied?: boolean) {
    this.loginTracking.track(LOGIN_STAGE.SIGNALR_INIT, 'Initiating SignalR negotiation');
    this.authApiService.signalRTokenApi(uuidv4(), { platform: this.platform, code_challange, access_denied, session_id: this.loginTracking.sessionId, device_id: this.loginTracking.deviceId }).subscribe({
      next: (response: any) => {        
        if (response?.data?.status === "SUCCESS") {
          this.b2cLoginStateBySignalR = response.data.state as string;
          const signaRHubEndpoint = response.data.connection.url;
          this.signalRToken = response.data.connection.accessToken;
          this.lastTrackedSignalRTokenRemainingSeconds = null;
          this.setServerTimeReferenceFromSignalRState();

          // Add tracking here
          this.loginTracking.track(
            LOGIN_STAGE.SIGNALR_TOKEN_RECEIVED,
            `SignalR token received successfully. signalRServerIssuedAtSeconds=${this.signalRServerIssuedAtSeconds}, signalRServerTimeReference=${this.signalRServerTimeReference}, hasToken=${!!this.signalRToken}, tokenLength=${this.signalRToken?.length ?? 0}, hasState=${!!this.b2cLoginStateBySignalR}, stateLength=${this.b2cLoginStateBySignalR?.length ?? 0}`
          );
          
          if (this.signalRToken && signaRHubEndpoint) {
            this.srHubconnection = new signalR.HubConnectionBuilder()
              .withUrl(signaRHubEndpoint, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
                accessTokenFactory: () => this.signalRToken,
              })
              .configureLogging(signalR.LogLevel.Information)
              .build();

            this.registerSignalRListener();
            this.startSignalRHubConnection(code_challange, logout);
          } else {
            console.error('SignalR Token or Endpoint not found');
            this.loginTracking.track(
              LOGIN_STAGE.REDIRECT_TO_B2C_FAILED, 
              'SignalR Token or Endpoint not found.'
            );
            this.showLoginErrorModal();
          }
        } else {          
          const errorMessage =
            response?.data?.message ||
            response?.data?.error ||
            response?.message ||
            'Unknown error';
          this.loginTracking.track(
            LOGIN_STAGE.REDIRECT_TO_B2C_FAILED, 
            `Error fetching SignalR token: ${errorMessage}`
          );
          console.error('Error fetching SignalR token:', errorMessage);
          this.showLoginErrorModal();
        }
      },
      error: (err: any) => {
        console.error('Error fetching SignalR token:', err);
        const errorMessage = this.getErrorMessage(err);
        this.loginTracking.track(
          LOGIN_STAGE.REDIRECT_TO_B2C_FAILED, 
          `Fetching SignalR token failed with error: ${err?.error?.error || 'unknown_error'}, description: ${errorMessage}`
        );
        this.showLoginErrorModal();
      },
    });
  }

  private async startSignalRConnection(): Promise<boolean> {
    const connection = this.srHubconnection;

    if (!connection) {
      console.warn(
        'startSignalRConnection: SignalR HubConnection is not initialized.'
      );

      return false;
    }

    // Another start operation is already in progress.
    if (this.signalRStartInProgress) {
      console.log(
        'startSignalRConnection: start already in progress. Skipping.'
      );

      this.loginTracking.track(
        LOGIN_STAGE.SIGNALR_CONNECTION_START_SKIPPED,
        'SignalR connection start skipped because another start operation is already in progress.'
      );

      return false;
    }

    // Only start when the connection is completely disconnected.
    if (
      connection.state !== signalR.HubConnectionState.Disconnected
    ) {
      console.log(
        `startSignalRConnection: start skipped. Current state: ${connection.state}.`
      );

      this.loginTracking.track(
        LOGIN_STAGE.SIGNALR_CONNECTION_START_SKIPPED,
        `SignalR connection start skipped. Current state: ${connection.state}.`
      );

      return connection.state === signalR.HubConnectionState.Connected;
    }

    this.signalRStartInProgress = true;

    try {
      this.loginTracking.track(
        LOGIN_STAGE.SIGNALR_CONNECTION_START_ATTEMPT,
        'Attempting to start SignalR connection.'
      );

      await connection.start();

      // To prevents a stale retry from firing after the connection has already recovered.
      this.clearSignalRRetryTimeout();

      console.log(
        `startSignalRConnection: connection started. State: ${connection.state}`
      );

      this.loginTracking.track(
        LOGIN_STAGE.SIGNALR_CONNECTION_STARTED,
        `SignalR connection started successfully. State: ${connection.state}`
      );

      return true;

    } catch (err) {

      const errorMessage = this.getErrorMessage(err);

      console.error(
        'startSignalRConnection: connection start failed:',
        errorMessage
      );

      this.loginTracking.track(
        LOGIN_STAGE.SIGNALR_CONNECTION_FAILED,
        `SignalR connection failed to start with error: ${errorMessage}`
      );

      return false;

    } finally {
      this.signalRStartInProgress = false;
    }
  }

  private clearSignalRRetryTimeout(): void {
    if (this.signalRRetryTimeout) {
      clearTimeout(this.signalRRetryTimeout);
      this.signalRRetryTimeout = null;
    }
  }

  async startSignalRHubConnection(
    code_challange: string,
    logout?: boolean
  ): Promise<void> {

    try {

      const connected = await this.startSignalRConnection();

      if (!connected) {

        console.warn(
          'startSignalRHubConnection: SignalR connection could not be established.'
        );

        // Avoid creating multiple retry timers.
        if (!this.signalRRetryTimeout) {

          this.signalRRetryTimeout = setTimeout(() => {

            this.signalRRetryTimeout = null;

            this.startSignalRHubConnection(
              code_challange,
              logout
            );

          }, 5000);
        }

        return;
      }

      this.browserFinished = false;

      await this.handleSignalRConnected(
        code_challange,
        logout
      );

    } catch (err) {

      console.error(
        'startSignalRHubConnection error:',
        err
      );

      const errorMessage = this.getErrorMessage(err);

      this.loginTracking.track(
        LOGIN_STAGE.SIGNALR_CONNECTION_FAILED,
        `SignalR HUB connection failed to start with error: ${errorMessage}`
      );
    }
  }

  private async handleSignalRConnected(
    code_challange: string,
    logout?: boolean
  ): Promise<void> {

    this.startConnectionTimer();
    this.browserFinished = false;

    if (logout) {

      const state_data = jwtDecode<StateData>(
        this.b2cLoginStateBySignalR
      );

      const access_denied = state_data['access_denied'];

      if (access_denied) {
        this.lastUrl = '';
        this.browserFinished = true;
        await Browser.close();
        this.signalRToken = '';
        await this.srHubconnection?.stop();
        this.b2cLoginStateBySignalR = '';
        this.connectionTimeSubscription?.unsubscribe();
        this.loginTracking.track(
          LOGIN_STAGE.SIGNALR_CONNECTION_FAILED,
          'SignalR connection failed due to access denied.'
        );
        this.router.navigate(['access-denied']);
      } else {
        const logoutUrl = this.getB2CLogoutUrl(
          this.b2cLoginStateBySignalR
        );
        // Avoid logging the complete URL as it may contain sensitive parameters.
        console.log(
          'startSignalRHubConnection: opening B2C logout browser.'
        );

        this.lastUrl = logoutUrl;
        this.loginTracking.track(
          LOGIN_STAGE.BROWSER_OPENED,
          'Browser opened for B2C logout.'
        );
        await this.openBrowser(this.lastUrl);
      }

    } else {

      const loginUrl = this.getB2CLoginUrl(
        this.b2cLoginStateBySignalR,
        code_challange
      );

      // Avoid logging the complete URL as it may contain sensitive parameters.
      console.log(
        'startSignalRHubConnection: opening B2C login browser.'
      );

      this.lastUrl = loginUrl;
      this.loginTracking.track(
        LOGIN_STAGE.BROWSER_OPENED,
        'Browser opened for B2C login.'
      );
      await this.openBrowser(this.lastUrl);
    }
  }

  async startConnectionTimer() {
    console.log('startConnectionTimer called');

    // Make sure an existing timer is not running.
    this.connectionTimeSubscription?.unsubscribe();

    let expiryCheckCount = 0;
    const maxExpiryChecks = 3;

    this.signalRConnectionCheckInProgress = false;
    this.signalRTimerWasOffline = false;

    this.loginTracking.track(
      LOGIN_STAGE.SIGNALR_TIMER_STARTED,
      'SignalR connection monitoring timer started. Interval: 500ms.'
    );

    this.connectionTimeSubscription = interval(500).subscribe(async () => {

      // Prevent overlapping async timer executions.
      if (this.signalRConnectionCheckInProgress) {
        return;
      }

      this.signalRConnectionCheckInProgress = true;

      try {

        // No network or SignalR token available.
        if (!this.isOnlineSubject.value || !this.signalRToken) {

          if (!this.isOnlineSubject.value) {

            // Track only when the device transitions to offline.
            if (!this.signalRTimerWasOffline) {
              this.signalRTimerWasOffline = true;

              this.loginTracking.track(
                LOGIN_STAGE.SIGNALR_TIMER_SKIPPED,
                'SignalR timer check skipped because device is offline.'
              );
            }

          } else {
            // Network is available again.
            this.signalRTimerWasOffline = false;
          }

          return;
        }

        // Network and SignalR token are available again.
        this.signalRTimerWasOffline = false;

        // Check whether the SignalR token has expired.
        const isExpired = this.isSignalRTokenExpired();

        if (isExpired) {

          expiryCheckCount++;

          // Don't immediately restart the login flow.
          // Require multiple consecutive expiry detections.
          this.loginTracking.track(
            LOGIN_STAGE.SIGNALR_TOKEN_EXPIRED,
            `SignalR token expiry detected. Consecutive count: ${expiryCheckCount}/${maxExpiryChecks}.`
          );

          if (expiryCheckCount < maxExpiryChecks) {
            return;
          }

          this.loginTracking.track(
            LOGIN_STAGE.SIGNALR_TOKEN_EXPIRY_CONFIRMED,
            'SignalR token expiry confirmed after consecutive checks.'
          );

          console.warn(
            'AuthService: SignalR token expiry confirmed.'
          );

          // Clear the expired token before restarting the login flow.
          this.signalRToken = '';

          // Stop the current monitoring timer.
          this.connectionTimeSubscription?.unsubscribe();
          this.connectionTimeSubscription = undefined;

          if (!this.browserFinished) {

            this.browserFinished = true;

            try {
              await Browser.close();

              this.loginTracking.track(
                LOGIN_STAGE.BROWSER_CLOSED,
                'B2C browser closed successfully after SignalR token expiry.'
              );

            } catch (err) {

              const errorMessage = this.getErrorMessage(err);

              console.warn(
                'AuthService: Error closing B2C browser:',
                errorMessage
              );

              this.loginTracking.track(
                LOGIN_STAGE.BROWSER_CLOSE_FAILED,
                `B2C browser close failed after SignalR token expiry. Error: ${errorMessage}`
              );
            }
          }

          // Show the login error modal to re-start the login
          this.showLoginErrorModal();

          return;
        }

        // Token is valid.
        expiryCheckCount = 0;

        // No SignalR connection available.
        if (!this.srHubconnection) {
          return;
        }

        const connectionState = this.srHubconnection.state;

        // Skip restrat if connection is already connected or in progress
        if (
          connectionState === signalR.HubConnectionState.Connected ||        
          connectionState === signalR.HubConnectionState.Connecting ||
          connectionState === signalR.HubConnectionState.Reconnecting ||
          connectionState === signalR.HubConnectionState.Disconnecting
        ) {
          return;
        }

        // Connection needs to be restarted.
        this.loginTracking.track(
          LOGIN_STAGE.SIGNALR_NOT_CONNECTED,
          `SignalR connection is not connected. Current state: ${connectionState}. Attempting reconnection.`
        );

        console.log(
          `Restarting SignalR connection. Current state: ${connectionState}`
        );

        const restarted = await this.startSignalRConnection();

        if (restarted) {

          this.loginTracking.track(
            LOGIN_STAGE.SIGNALR_CONNECTION_RESTARTED,
            'SignalR connection restarted successfully.'
          );

        } else {

          this.loginTracking.track(
            LOGIN_STAGE.SIGNALR_CONNECTION_RESTART_FAILED,
            'SignalR connection restart failed.'
          );
        }

      } finally {
        // Always release the execution lock.
        this.signalRConnectionCheckInProgress = false;
      }
    });
  }

  registerSignalRListener() {
    this.srHubconnection?.on('newAuthCode', async (code: string, state: string) => {
      try {
        const statMatched = this.b2cLoginStateBySignalR && state === this.b2cLoginStateBySignalR;
        this.loginTracking.track(
          LOGIN_STAGE.AUTH_CODE_RECEIVED_INVOKED,
          `Auth code received via SignalR. hasCode=${!!code}, codeLength=${code?.length ?? 0}, hasState=${!!state}, stateLength=${state?.length ?? 0}, statMatched: ${statMatched}`,        
        );

        this.lastUrl = '';
        this.browserFinished = true;

        // Close browser — remove listener first so closing doesn't re-trigger onBrowserFinished
        await Browser.removeAllListeners();
        this.browserListenerRegistered = false;
        await Browser.close();

        this.signalRToken = '';
        await this.srHubconnection?.stop();
        this.b2cLoginStateBySignalR = '';
        this.connectionTimeSubscription?.unsubscribe();
        console.log('SignalR connection stopped and browser closed');

        if (code && statMatched) {
          this.loginTracking.track(LOGIN_STAGE.AUTH_CODE_RECEIVED_SUCCESS, 'Auth code received successfully.');
          this.b2cAuthCodeBySignalR = code;
          this.acquireB2CAccessToken(code);
        } else {
          console.error('Auth code invalid or state mismatch');
          this.loginTracking.track(LOGIN_STAGE.AUTH_CODE_RECEIVED_FAILED, 'Auth code receiving failed due to invalid code or state mismatch');
          this.showLoginErrorModal();
        }
      } catch(err){
        this.lastUrl = '';
        this.browserFinished = true;        

        this.signalRToken = '';
        await this.srHubconnection?.stop();
        this.b2cLoginStateBySignalR = '';
        this.connectionTimeSubscription?.unsubscribe();

        console.error('Receiving auth code failed with error:', err);
        const errorMessage = this.getErrorMessage(err);
        this.loginTracking.track(LOGIN_STAGE.AUTH_CODE_RECEIVED_FAILED, `Receiving auth code failed with error: ${errorMessage}`);
        this.showLoginErrorModal();

        // Close browser — remove listener first so closing doesn't re-trigger onBrowserFinished
        await Browser.removeAllListeners();
        this.browserListenerRegistered = false;
        await Browser.close();
      }
    });
  }

  authGuard() {
    try {
      console.log('AuthService: authGuard called');
      const encAccessToken: any = this._storageService.getDataFromStorage('access_token');

      const decrypted = encAccessToken ? this.crewSelfService.decryptData(encAccessToken) : {};

      if (!decrypted?.access_token) {
        console.log('AuthService: AuthGuard > failed. Token missing');
        this.signIn();
        return false;
      }

      console.log('AuthService: AuthGuard > validated.');
      return true;
    } catch (err) {
      console.log('AuthService: authGuard token error', err);
      this.signOut();
      return false;
    }
  }


  getB2CLoginUrl(state: string, code_challenge: string) {
    const url = (
      AppConfig.settings?.B2C_LOGIN_AUTHORITY +
      '/oauth2/v2.0/authorize' +
      `?client_id=${encodeURIComponent(AppConfig.settings?.B2C_CLIENT_ID ?? '')}` +
      `&response_type=code` +
      `&redirect_uri=${encodeURIComponent(AppConfig.settings?.B2C_REDIRECT_URI ?? '')}` +
      `&response_mode=query` +
      `&scope=${encodeURIComponent(AppConfig.settings?.B2C_USER_LOGIN_SCOPE ?? '')}` +
      `&code_challenge=${encodeURIComponent(code_challenge)}` +
      `&state=${encodeURIComponent(state)}` +
      `&code_challenge_method=S256`
    );

    return url;
  }


  getB2CLogoutUrl(state: string) {
    const url = (
      AppConfig.settings?.B2C_LOGIN_AUTHORITY +
      '/oauth2/v2.0/logout' +
      `?post_logout_redirect_uri=${encodeURIComponent(AppConfig.settings?.B2C_LOGOUT_REDIRECT_URI ?? '')}` +
      `&state=${encodeURIComponent(state)}`
    );

    return url;
  }

  signOut(isAccessDenied = false) {
    console.log(
      'AuthService: signOut called.',
      'isLoginInProgress:', this.isLoginInProgress,
      'signOutInProgress:', this.signOutInProgress
    );

    if (this.isLoginInProgress || this.signOutInProgress) {
      console.warn(
        'Sign-out skipped because authentication transaction is already in progress'
      );

      this.loginTracking.track(
        LOGIN_STAGE.SIGNOUT_SKIPPED,
        'Sign-out skipped because authentication transaction is already in progress'
      );

      return;
    }

    // IMPORTANT: set synchronously before calling anything else
    this.signOutInProgress = true;

    this._storageService.clearStorage();

    console.log('AuthService: Cleared storage and calling signIn.');

    if (isAccessDenied && !this.isNative) {
      this.router.navigate(['access-denied']);
      this.signOutInProgress = false;
    } else {
      this.signIn(true, isAccessDenied);
    }
  }

  async signIn(logoutFromB2C = false, isAccessDenied = false) {
    console.log("AuthService: signIn called. logoutFromB2C =", logoutFromB2C);

    if (this.isLoginInProgress) {
      console.warn('Login skipped as already in progress from SignIN function');

      this.loginTracking.track(
        LOGIN_STAGE.LOGIN_SKIPPED,
        'Login skipped as already in progress from SignIN function'
      );

      return;
    }

    this.isLoginInProgress = true;
    this.loginTracking.startNewSession();

    this.loginTracking.track(
      LOGIN_STAGE.LOGIN_INIT,
      `Login initiated. platform=${this.platform}, logout=${logoutFromB2C}, access_denied=${isAccessDenied}.`
    );

    if (this.platform === 'android') {
      try{
        const batteryOptimization =
          await BatteryOptimization.isBatteryOptimizationEnabled();

        console.log(
          "AuthService: Battery optimization check result:",
          batteryOptimization 
        );

        if (batteryOptimization.enabled) {
          console.warn(
            "AuthService: Battery optimization is enabled. Sign-in aborted."
          );

          this.loginTracking.track(
            LOGIN_STAGE.LOGIN_ABORTED,
            `Login aborted due to battery optimazation found enabled on Android device.`
          );

          // Login did not proceed, release sign-out and login lock
          this.signOutInProgress = false;
          this.isLoginInProgress = false;

          this.showLoginErrorModal(
            '',
            'Battery Optimization Enabled',
            'Battery optimization is enabled on your Android device and may prevent the app from completing the login process. Please disable battery optimization for the CSS app and try logging in again.'          
          );

          return;
        }
      } catch(err){
        const errorMessage = this.getErrorMessage(err);
        console.log(
          `AuthService: failed to check battery optimization setting with error: ${errorMessage}`
        );

        this.loginTracking.track(
          LOGIN_STAGE.LOGIN_ABORTED,
          `Failed to check battery optimization setting with error: ${errorMessage}`
        );

        // Login did not proceed, release sign-out and login lock
        this.signOutInProgress = false;
        this.isLoginInProgress = false;

        this.showLoginErrorModal(
          '',
          'Unable to Check Device Settings',
          'We couldn\'t verify the battery optimization settings on your Android device. Please try logging in again. If the problem continues, please restart the app and try again.'
        );

        return;
      }
    }

    if (!this.isOnlineSubject.value) {
      console.warn("AuthService: No internet connection. Sign-in aborted.");

      this.loginTracking.track(
        LOGIN_STAGE.LOGIN_FAILED,
        'Login failed because device is offline'
      );

      // Login did not proceed, release login and sign-out lock
      this.signOutInProgress = false;
      this.isLoginInProgress = false;

      return;
    }

    try {
      const response = await this.generateCodeVerifierAndChallenge();

      if (this.isNative) {
        console.log(
          "AuthService: Native platform detected. Redirecting to B2C login."
        );

        this.b2cCodeVerifier = response.codeVerifier;

        this.router.navigate(['auth']);

        this.redirectToB2c(
          response.codeChallenge,
          logoutFromB2C,
          isAccessDenied
        );

      } else {
        console.log(
          "AuthService: Web platform detected. Calling webTokenApi."
        );

        this.authApiService.webTokenApi(uuidv4(), {
          platform: this.platform,
          code_challange: response.codeChallenge,
          access_denied: isAccessDenied,
          session_id: this.loginTracking.sessionId,
          device_id: this.loginTracking.deviceId
        }).subscribe({
          next: (res: any) => {
            console.log(
              "AuthService: Received response from webTokenApi:",
              res
            );

            this.loginTracking.track(
              LOGIN_STAGE.WEB_TOKEN_SUCCESS,
              'Web token negotiated, user redirected to B2C.'
            );

            this._storageService.putDataInStorage(
              'code_verifier',
              this.crewSelfService.encryptData({
                code_verifier: response.codeVerifier
              })
            );

            const redirectUrl = logoutFromB2C
              ? this.getB2CLogoutUrl(res.state)
              : this.getB2CLoginUrl(
                  res.state,
                  response.codeChallenge
                );

            window.location.href = redirectUrl;
          },

          error: (err: any) => {
            console.error(
              "AuthService: Error from webTokenApi:",
              err
            );

            const errorMessage = this.getErrorMessage(err);

            this.loginTracking.track(
              LOGIN_STAGE.WEB_TOKEN_FAILED,
              `Generate web token failed with error: ${errorMessage}`
            );

            // Login transaction has failed
            this.isLoginInProgress = false;
            this.signOutInProgress = false;

            this.showLoginErrorModal();
          }
        });
      }

    } catch (err) {
      console.error(
        "AuthService: Error generating PKCE challenge:",
        err
      );

      // Login transaction did not start
      this.isLoginInProgress = false;
      this.signOutInProgress = false;

      this.loginTracking.track(
        LOGIN_STAGE.LOGIN_FAILED,
        `Login failed while generating PKCE challenge: ${this.getErrorMessage(err)}`
      );

      this.showLoginErrorModal();
    }
  }

  async generateCodeVerifierAndChallenge() {
    const codeVerifier = this.generateRandomString(128);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    return { codeVerifier, codeChallenge };
  }


  // Helper function to generate a random string of a given length  
  generateRandomString(length: number) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let randomString = '';
    const randomValues = new Uint8Array(length);
    window.crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
      randomString += charset[randomValues[i] % charset.length];
    }

    console.log(`AuthService: Generated random string of length ${length}`);
    return randomString;
  }


  // Helper function to generate a code_challenge from a code_verifier  
  async generateCodeChallenge(codeVerifier: string) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(codeVerifier);
      const digest = await window.crypto.subtle.digest('SHA-256', data);

      console.log("AuthService: SHA-256 digest generated for code_verifier.");
      return this.base64UrlEncode(new Uint8Array(digest));
    } catch (error) {
      console.error("AuthService: Failed to generate code_challenge:", error);
      throw error;
    }
  }


  base64UrlEncode(buffer: Uint8Array) {
    let base64 = btoa(String.fromCharCode(...buffer)).replace(/\+/g, '-').replace(/\//g, '_');
    let i = base64.length - 1;
    while (i >= 0 && base64[i] === '=') {
      i--;
    }
    base64 = base64.substring(0, i + 1);
    return base64;
  }

  refreshAccessToken(): Observable<string> {
    if (this.refreshTokenInProgress) {
      console.log("AuthService: refreshAccessToken() already in progress.");
      return this.refreshTokenSubject.asObservable() as any;
    } else {
      this.refreshTokenInProgress = true;

      const enc_refresh_token: any = this._storageService.getDataFromStorage('refresh_token');

      let refresh_token: string;
      try {
        const decrypted = this.crewSelfService.decryptData(enc_refresh_token);
        refresh_token = decrypted.refresh_token;
      } catch (err) {
        console.error("AuthService: Error decrypting refresh token:", err);
        this.refreshTokenInProgress = false;
        return throwError(() => err);
      }

      return this.authApiService.b2cRefreshTokenApi(refresh_token).pipe(
        tap((response: any) => {
          this._storageService.putDataInStorage(
            'access_token',
            this.crewSelfService.encryptData({ access_token: response.access_token })
          );
          this._storageService.putDataInStorage(
            'refresh_token',
            this.crewSelfService.encryptData({ refresh_token: response.refresh_token })
          );

          this.refreshTokenInProgress = false;
        }),
        map(response => response.access_token),
        catchError(err => {
          console.error("AuthService: Error during token refresh:", err);
          this.refreshTokenInProgress = false;
          return throwError(() => err);
        })
      );
    }
  }

  async showLoginErrorModal(loginState: string = '', title: string = 'Login error', message: string='Something went wrong, please try logging in again.', hideLoginAgainLink: boolean=false) {
    this.isLoginInProgress = false;
    this.signOutInProgress = false;
    const modal = this.modalController.create({
      component: ErrorModalComponent,
      cssClass: 'delete-modal',
      componentProps: {
        title,
        message,
        loginState: loginState,
        hideLoginAgainLink
      },
      backdropDismiss: false
    });

    (await modal).present();
  }

  validateB2CToken(token: string, session_id?: string): boolean {
    if (!token) {
      console.warn('AuthService: validateB2CToken > token is empty.');
      this.loginTracking.track(LOGIN_STAGE.TOKEN_VALIDATION_FAILED, 'Token is empty.', session_id);
      return false;
    }

    try {
      const tokenInfo: any = jwtDecode(token);

      const hasUsername =
        !!tokenInfo['signInNames.userName'];

      const hasEmail =
        !!tokenInfo['signInNames.emailAddress'];

      console.log('AuthService: B2C token validation:', {
        hasUsername,
        hasEmail,
        hasExp: !!tokenInfo.exp,
        hasAud: !!tokenInfo.aud,
        hasIss: !!tokenInfo.iss
      });

      if (!hasUsername || !hasEmail) {
        console.error(
          'AuthService: B2C token validation failed > required claims missing:',
          {
            hasUsername,
            hasEmail
          }
        );
        this.loginTracking.track(
          LOGIN_STAGE.TOKEN_VALIDATION_FAILED,
          `Required claims missing. hasUsername=${hasUsername}, hasEmail=${hasEmail}, hasExp=${!!tokenInfo.exp}, hasAud=${!!tokenInfo.aud}, hasIss=${!!tokenInfo.iss}`,
          session_id
        );
        return false;
      }

      this.loginTracking.track(
        LOGIN_STAGE.TOKEN_VALIDATION_SUCCESS,
        `Required claims present. hasUsername=${hasUsername}, hasEmail=${hasEmail}`,
        session_id
      );
      return true;

    } catch (err) {
      console.error(
        'AuthService: B2C token validation failed > JWT decode error:',
        err
      );
      this.loginTracking.track(
        LOGIN_STAGE.TOKEN_VALIDATION_FAILED, 
        `B2C token validation failed > JWT decode error:`,
        session_id
      );

      return false;
    }
  }

  isSignalRTokenExpired(): boolean {

    const token = this.signalRToken;

    if (!token) {
      console.warn(
        'AuthService: SignalR token not found. Considered expired.'
      );

      this.loginTracking.track(
        LOGIN_STAGE.SIGNALR_TOKEN_EXPIRY_CHECK,
        'SignalR token expiry check failed because token is missing. Token considered expired.'
      );

      return true;
    }

    try {

      const tokenInfo: any = jwtDecode(token);
      const expiry = Number(tokenInfo?.exp);

      if (!expiry || Number.isNaN(expiry)) {
        console.warn(
          'AuthService: SignalR token does not contain a valid exp claim.'
        );

        this.loginTracking.track(
          LOGIN_STAGE.SIGNALR_TOKEN_EXPIRY_CHECK,
          `SignalR token expiry check failed because exp claim is missing or invalid. hasExp=${!!tokenInfo?.exp}`
        );

        return true;
      }

      if (
        !this.signalRServerIssuedAtSeconds ||
        !this.signalRServerTimeReference
      ) {
        console.warn(
          'AuthService: Server time reference is not available.'
        );

        this.loginTracking.track(
          LOGIN_STAGE.SIGNALR_TOKEN_EXPIRY_CHECK,
          'SignalR token expiry check failed because server time reference is unavailable.'
        );

        return true;
      }

      // Calculate elapsed time using a monotonic clock.
      // This is not affected by changes to the device date/time.
      const elapsedSeconds =
        (performance.now() - this.signalRServerTimeReference) / 1000;

      // Estimate current server time using:
      // server-issued timestamp + elapsed time.
      const serverNow =
        this.signalRServerIssuedAtSeconds + elapsedSeconds;

      const remainingSeconds =
        expiry - serverNow;

      const gracePeriodSeconds = 10;

      const isExpired =
        serverNow >= (expiry + gracePeriodSeconds);

      const shouldTrack =
        isExpired ||
        (
          remainingSeconds >= 0 &&
          Math.floor(remainingSeconds) % 30 === 0 &&
          Math.floor(remainingSeconds) !==
            this.lastTrackedSignalRTokenRemainingSeconds
        );

      if (shouldTrack) {

        const trackedRemainingSeconds =
          Math.floor(remainingSeconds);

        this.lastTrackedSignalRTokenRemainingSeconds =
          trackedRemainingSeconds;

        this.loginTracking.track(
          LOGIN_STAGE.SIGNALR_TOKEN_EXPIRY_CHECK,
          `SignalR token timing. expiry=${expiry}, serverNow=${Math.floor(serverNow)}, remainingSeconds=${trackedRemainingSeconds}, gracePeriodSeconds=${gracePeriodSeconds}, isExpired=${isExpired}`
        );
      }

      console.log(
        'AuthService: SignalR token timing:',
        {
          expiry,
          serverNow: Math.floor(serverNow),
          remainingSeconds: Math.floor(remainingSeconds),
          gracePeriodSeconds,
          isExpired
        }
      );

      return isExpired;

    } catch (err) {

      const errorMessage = this.getErrorMessage(err);

      console.error(
        'AuthService: Failed to evaluate SignalR token expiry:',
        errorMessage
      );

      this.loginTracking.track(
        LOGIN_STAGE.SIGNALR_TOKEN_EXPIRY_CHECK,
        `SignalR token expiry check failed because token could not be evaluated. errorType=${err?.constructor?.name ?? 'unknown'}`
      );

      // Fail closed if the token cannot be evaluated.
      return true;
    }
  }

  setServerTimeReferenceFromSignalRState(): void {
    try {
      const stateInfo: any = jwtDecode(this.b2cLoginStateBySignalR);

      const serverIssuedAtMilliseconds = Number(stateInfo?.iat);

      if (
        serverIssuedAtMilliseconds &&
        !Number.isNaN(serverIssuedAtMilliseconds)
      ) {
        this.signalRServerIssuedAtSeconds =
          serverIssuedAtMilliseconds / 1000;

        // Monotonic client timer. Not affected by device date/time changes.
        this.signalRServerTimeReference = performance.now();

      } else {
        console.warn(
          'AuthService: SignalR state does not contain a valid iat.'
        );

        this.signalRServerIssuedAtSeconds = 0;
        this.signalRServerTimeReference = 0;
      }

    } catch (err) {
      const errorMessage = this.getErrorMessage(err);

      console.warn(
        'AuthService: Failed to decode SignalR state:',
        errorMessage
      );

      this.signalRServerIssuedAtSeconds = 0;
      this.signalRServerTimeReference = 0;
    }
  }

  private getErrorMessage(err: any): string {
    if (typeof err === 'string') {
      return err;
    }

    if (err === null || err === undefined) {
      return 'Unknown error';
    }

    if (typeof err === 'number') {
      return String(err);
    }

    // Angular HttpErrorResponse where error itself is a string
    if (typeof err?.error === 'string') {
      return err.error;
    }

    return (
      err?.error?.error_description ||
      err?.error_description ||
      err?.error?.message ||
      err?.error?.error ||
      err?.message ||
      (
        err?.status !== undefined && err?.status !== null
          ? `HTTP ${err.status}: ${err.statusText || 'Unknown error'}`
          : 'Unknown error'
      )
    );
  }
}